import React, { useState, useMemo } from 'react'
import { Formik, Form, FormikProps, FieldArray } from 'formik'
import * as Yup from 'yup'

import { useOfferionTranslation } from '../../../shared/store/hooks/useOfferionTranslation'
import { useStaticCountryOptions } from '../../../shared/hooks/useCountryOptions'
import { Currency } from '../../../shared/models'
import { useCurrencyOptions } from '../../../shared/ui/currency'
import { OnboardingCompanyData, useOnboardingStore } from '../../../shared/store/onboardingStore'
import { useUserStore } from '../../../shared/store/userStore'
import { useCompanyStore } from '../../../shared/store/companyStore'
import { Spinner } from '../../../shared/../components/ui/SpinnerLoader'
import { StepIndicator } from './step-indicator'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog'
import { Button } from '../../shared/Button'
import { FormField } from '../../shared/form/FormField'
import { FormInput } from '../../shared/form/FormInput'
import { FormSelect } from '../../shared/form/FormSelect'
import { FormCombobox } from '../../shared/form/FormCombobox'
import { FormRadioInput } from '../../shared/form/FormRadioInput'
import { validateIban } from '../../../shared/utils/validators'
import { CircleWavyCheck, Plus } from './icons'
import { CurrencyInput } from '../../../shared/forms/CurrencyInput'
import { currencyAsText } from '../../../shared/utils/formatters'
import { CompanySettingsApi } from '../../../services/CompanySettingsApi'
import { getBankInfo } from '../../utils/bank'

// ensure that legalCompanyName is max 2000 chars, if it's too long it won't get saved into the DB.
const MAX_LEGAL_COMPANY_NAME_LENGTH = 2000

export const OnboardingMultiStepModal = () => {
    const isOnboardingModalOpen = useOnboardingStore(state => state.isOnboardingModalOpen)
    const isLoadingOnboardingData = useOnboardingStore(state => state.isLoadingOnboardingData)
    const closeOnboardingModal = useOnboardingStore(state => state.closeOnboardingModal)
    // TODO - move these to children components instead of drilling down
    const onboardingData = useOnboardingStore(state => state.onboardingData)
    const isSubmittingOnboardingData = useOnboardingStore(state => state.isSubmittingOnboardingData)
    const updateOnboardingDataStore = useOnboardingStore(state => state.updateOnboardingData)
    const setShowOnboardingOnStart = useOnboardingStore(state => state.setShowOnboardingOnStart)

    const updateLanguage = useUserStore(state => state.updateLanguage)
    const setCompanySettings = useCompanyStore(state => state.setCompanySettings)

    const updateOnboardingData = async (partialOnboardingData: Partial<OnboardingCompanyData>, skipSuccessToast?: boolean) => {
        await updateOnboardingDataStore(partialOnboardingData, updateLanguage, setCompanySettings, skipSuccessToast)
    }

    if (!isOnboardingModalOpen) return null

    return (
        <Dialog open={isOnboardingModalOpen} onOpenChange={closeOnboardingModal}>
            <DialogContent>
                {isLoadingOnboardingData ? (
                    <div style={{ padding: '4rem', textAlign: 'center', paddingTop: '4rem' }}>
                        <Spinner />
                    </div>
                ) : (
                    // .v2-root is here to reset the old styles and simplify styling of the new components.
                    <div className="v2-root">
                        <MultiStepForm
                            onboardingData={onboardingData!}
                            updateOnboardingData={updateOnboardingData}
                            closeOnboardingModal={closeOnboardingModal}
                            isSubmittingOnboardingData={isSubmittingOnboardingData}
                            setShowOnboardingOnStart={setShowOnboardingOnStart}
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}

interface MultiStepFormProps {
    onboardingData: OnboardingCompanyData
    closeOnboardingModal: () => void
    isSubmittingOnboardingData: boolean
    updateOnboardingData: (partialOnboardingData: Partial<OnboardingCompanyData>, skipSuccessToast?: boolean) => Promise<void>
    setShowOnboardingOnStart: (show: boolean) => void
}

type StepConfig = {
    // Step ID
    id: string
    // Internal title and subtitle translation keys
    title: string | ((formValues: OnboardingCompanyData) => string)
    subtitle: string | ((formValues: OnboardingCompanyData) => string)
    // Function that determines if the step should be skipped based on current form values
    // For example for Croatia without Tax the TaxAndCurrency step is skipped
    // Receives the current form values to make dynamic decisions
    shouldSkip: (formValues: OnboardingCompanyData) => boolean
    // what fields are included in this step
    fields: (keyof OnboardingCompanyData)[]
    // The Yup validation schema for this step
    validationSchema: Yup.ObjectSchema
    // The render function for this step
    render: (formikProps: FormikProps<OnboardingCompanyData>) => React.ReactNode
    // Optional function to transform step values before submitting to backend
    // This allows each step to have custom logic for data transformation
    beforeSubmit?: (
        stepValues: Partial<OnboardingCompanyData>,
        allFormValues: OnboardingCompanyData,
        onboardingData: OnboardingCompanyData
    ) => Partial<OnboardingCompanyData>
}

const MultiStepForm: React.FC<MultiStepFormProps> = ({
    closeOnboardingModal,
    isSubmittingOnboardingData,
    onboardingData,
    updateOnboardingData,
    setShowOnboardingOnStart,
}) => {
    const { t } = useOfferionTranslation()
    const [currentStepIndex, setCurrentStepIndex] = useState(0)
    const [formValues, setFormValues] = useState<OnboardingCompanyData>(initializeFormValues(onboardingData))

    /**
     * Scrolls the modal to the top when navigating between steps
     */
    const scrollModalToTop = () => {
        setTimeout(() => {
            document.querySelector('.popup__overlay.popup__overlay--show')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        }, 10)
    }

    /**
     * Extracts only the fields relevant to the current step from form values
     */
    const extractStepValues = (fields: (keyof OnboardingCompanyData)[], values: OnboardingCompanyData) => {
        return fields.reduce((acc, field) => {
            // @ts-ignore
            acc[field] = values[field]
            return acc
        }, {} as Partial<OnboardingCompanyData>)
    }

    /**
     * Marks all fields in the current step as touched for validation display
     * Handles both regular fields and array fields (like bankAccounts)
     */
    const markStepFieldsAsTouched = (fields: (keyof OnboardingCompanyData)[], values: OnboardingCompanyData) => {
        return fields.reduce((acc, field) => {
            const fieldValue = values[field]
            if (Array.isArray(fieldValue)) {
                acc[field] = (fieldValue as any[]).map(() => true)
            } else {
                acc[field] = true
            }
            return acc
        }, {} as any)
    }

    const croatianIbanValidation = Yup.string()
        .nullable()
        .test('valid-oib', t('V2.Onboarding.Step2.ibanInvalidFormat'), value => {
            // we don't want to block empty values here, just the invalid ones
            if (value === '' || value === undefined) return true

            if (value && typeof value === 'string') {
                return validateIban(value)
            } else {
                return false
            }
        })
    const regularIbanValidation = Yup.string().nullable()

    const bankAccountSchema = onboardingData.country === 'HR' ? croatianIbanValidation : regularIbanValidation

    // Step Configuration Array - all step metadata in one place
    // Memoized to prevent unnecessary re-creation of validation schemas on every render
    const stepConfigs: StepConfig[] = useMemo(
        () => [
            {
                id: 'company-and-address',
                title: 'V2.Onboarding.Step1.title',
                subtitle: 'V2.Onboarding.Step1.subtitle',
                shouldSkip: formValues => false,
                fields: ['name', 'companyLegalType', 'street', 'streetNumber', 'city', 'country', 'postalCode'],
                validationSchema: Yup.object({
                    name: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step1.companyName') })),
                    companyLegalType: Yup.string().when('country', {
                        is: 'HR',
                        then: (schema: any) =>
                            schema.required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step1.companyLegalType') })),
                        otherwise: (schema: any) => schema.nullable(),
                    }),
                    street: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step1.streetName') })),
                    streetNumber: Yup.string().required(
                        t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step1.streetNumber') })
                    ),
                    city: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step1.city') })),
                    postalCode: Yup.string()
                        .required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step1.postalCode') }))
                        .max(10)
                        .nullable(),
                    country: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step1.country') })),
                }),
                render: (formikProps: FormikProps<OnboardingCompanyData>) => <CompanyAndAddressStep formikProps={formikProps} />,
            },
            {
                id: 'contact-info',
                title: 'V2.Onboarding.Step2.title',
                subtitle: 'V2.Onboarding.Step2.subtitle',
                shouldSkip: formValues => false,
                fields: ['telephone', 'mobile', 'contactEmail', 'website', 'bankAccounts'],
                validationSchema: Yup.object({
                    telephone: Yup.string().nullable(),
                    mobile: Yup.string().nullable(),
                    contactEmail: Yup.string().email(t('Allaround.Validation.mail')).nullable(),
                    bankAccount: Yup.string().when('country', {
                        is: 'HR',
                        then: (schema: any) => croatianIbanValidation,
                        otherwise: (schema: any) => regularIbanValidation,
                    }),
                    bankAccounts: Yup.array().when('country', {
                        is: 'HR',
                        then: (schema: any) => schema.of(croatianIbanValidation),
                        otherwise: (schema: any) => schema.of(regularIbanValidation),
                    }),
                }),
                render: (formikProps: FormikProps<OnboardingCompanyData>) => <ContactInfoStep formikProps={formikProps} />,
                beforeSubmit: (stepValues, allFormValues, onboardingData) => {
                    // This is a hacky way to map bankAccounts to bankAccount
                    // when we finish the transition to multiple bankAccounts this can be cleaned up.
                    const bankAccounts = allFormValues.bankAccounts?.filter(ba => ba && ba.trim() !== '')
                    return {
                        ...stepValues,
                        bankAccounts: bankAccounts,
                        // Map the first item from bankAccounts to bankAccount
                        bankAccount: bankAccounts && bankAccounts.length > 0 ? bankAccounts[0] : '',
                    }
                },
            },
            {
                id: 'tax-and-currency',
                title: formValues => {
                    if (formValues.country === 'HR') {
                        return 'V2.Onboarding.Step3.titleTaxOnly'
                    } else if (onboardingData.isAccountingVat === false) {
                        return 'V2.Onboarding.Step3.titleCurrencyOnly'
                    } else {
                        return 'V2.Onboarding.Step3.title'
                    }
                },
                subtitle: formValues => {
                    if (formValues.country === 'HR') {
                        return 'V2.Onboarding.Step3.subtitleTaxOnly'
                    } else if (onboardingData.isAccountingVat === false) {
                        return 'V2.Onboarding.Step3.subtitleCurrencyOnly'
                    } else {
                        return 'V2.Onboarding.Step3.subtitle'
                    }
                },
                shouldSkip: formValues => formValues.country === 'HR' && onboardingData.isAccountingVat === false,
                fields: ['defaultCurrency', 'defaultVat'],
                validationSchema: Yup.object({
                    defaultCurrency: Yup.string().required(
                        t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step3.currency') })
                    ),
                    defaultVat: Yup.number()
                        .min(0, t('Allaround.Validation.incorrectVAT'))
                        .max(30, t('Allaround.Validation.incorrectVAT'))
                        .required(t('Allaround.Validation.required', { fieldName: t('Settings.Table.TaxCurrency.defaultVATInfoTitle') })),
                }),
                render: (formikProps: FormikProps<OnboardingCompanyData>) => (
                    <TaxAndCurrencyStep formikProps={formikProps} onboardingData={onboardingData} />
                ),
                beforeSubmit: (stepValues, allFormValues, onboardingData) => {
                    const updatedValues = { ...stepValues }

                    // If Croatia - defaultCurrency is always EUR
                    if (allFormValues.country === 'HR') {
                        updatedValues.defaultCurrency = Currency.EUR
                    }

                    // If Croatia and VAT in accounting is disabled - put it to default of 25, it won't be calculated anyway
                    if (onboardingData.isAccountingVat === false && allFormValues.country === 'HR') {
                        updatedValues.defaultVat = 25
                    }

                    return updatedValues
                },
            },
            {
                id: 'notes-and-legal',
                title: 'V2.Onboarding.Step4.title',
                subtitle: 'V2.Onboarding.Step4.subtitle',
                shouldSkip: formValues => formValues.country !== 'HR',
                fields: [
                    'vatOnCollectedPayments',
                    'workingWithIndividuals',
                    'commercialCourtName',
                    'companyRegistrationNumber',
                    'shareCapitalAmount',
                    'shareCapitalFullyPaid',
                    'unpaidShareCapitalAmount',
                    'boardMembers',
                    'doYouHaveAnAdvisoryBoard',
                    'advisoryBoardPresidentName',
                    'numberOfIssuedShares',
                    'nominalValuePerShare',
                    'differentAddressForOwnerAndTrade',
                    'tradeOwnerAddress',
                ],
                validationSchema: Yup.object({
                    vatOnCollectedPayments: Yup.string().oneOf(['yes', 'no']).required(),
                    workingWithIndividuals: Yup.string().oneOf(['yes', 'no']).required(),
                    commercialCourtName: Yup.string().when('companyLegalType', {
                        is: value => ['doo', 'jdoo', 'dd'].includes(value),
                        then: (schema: any) =>
                            schema.required(
                                t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.commercialCourtName') })
                            ),
                    }),
                    companyRegistrationNumber: Yup.string().when('companyLegalType', {
                        is: value => ['doo', 'jdoo', 'dd'].includes(value),
                        then: (schema: any) =>
                            schema.required(
                                t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.companyRegistrationNumber') })
                            ),
                    }),
                    shareCapitalAmount: Yup.string().when('companyLegalType', {
                        is: value => ['doo', 'jdoo', 'dd'].includes(value),
                        then: (schema: any) =>
                            schema.required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.shareCapitalAmount') })),
                    }),
                    shareCapitalFullyPaid: Yup.string().when('companyLegalType', {
                        is: value => ['doo', 'jdoo', 'dd'].includes(value),
                        then: (schema: any) =>
                            schema
                                .oneOf(['yes', 'no'])
                                .required(
                                    t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.shareCapitalFullyPaid') })
                                ),
                    }),

                    unpaidShareCapitalAmount: Yup.string().when('shareCapitalFullyPaid', {
                        is: 'no',
                        then: (schema: any) =>
                            schema.required(
                                t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.unpaidShareCapitalAmount') })
                            ),
                    }),
                    boardMembers: Yup.array().when('companyLegalType', {
                        is: value => ['doo', 'jdoo', 'dd'].includes(value),
                        then: (schema: any) =>
                            schema
                                .of(Yup.string())
                                .required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.boardMembers') })),
                    }),
                    doYouHaveAnAdvisoryBoard: Yup.string().when('companyLegalType', {
                        is: value => ['doo', 'jdoo', 'dd'].includes(value),
                        then: (schema: any) =>
                            schema
                                .oneOf(['yes', 'no'])
                                .required(
                                    t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.doYouHaveAnAdvisoryBoard') })
                                ),
                    }),
                    advisoryBoardPresidentName: Yup.string().when('doYouHaveAnAdvisoryBoard', {
                        is: 'yes',
                        then: (schema: any) =>
                            schema.required(
                                t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.advisoryBoardPresidentName') })
                            ),
                    }),
                    numberOfIssuedShares: Yup.string().when('companyLegalType', {
                        is: 'dd',
                        then: (schema: any) =>
                            schema.required(
                                t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.numberOfIssuedShares') })
                            ),
                    }),
                    nominalValuePerShare: Yup.string().when('companyLegalType', {
                        is: 'dd',
                        then: (schema: any) =>
                            schema.required(
                                t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.nominalValuePerShare') })
                            ),
                    }),
                    differentAddressForOwnerAndTrade: Yup.string().when('companyLegalType', {
                        is: 'obrt',
                        then: (schema: any) =>
                            schema.oneOf(['yes', 'no']).required(
                                t('Allaround.Validation.required', {
                                    fieldName: t('V2.Onboarding.Step4.differentAddressForOwnerAndTrade'),
                                })
                            ),
                    }),
                    tradeOwnerAddress: Yup.string().when('differentAddressForOwnerAndTrade', {
                        is: true,
                        then: (schema: any) =>
                            schema.required(t('Allaround.Validation.required', { fieldName: t('V2.Onboarding.Step4.tradeOwnerAddress') })),
                    }),
                }),
                render: (formikProps: FormikProps<OnboardingCompanyData>) => (
                    <NotesAndLegalStep formikProps={formikProps} onboardingData={onboardingData} />
                ),
                beforeSubmit: (stepValues, allFormValues, onboardingData) => {
                    // Convert vatOnCollectedPayments from 'yes'/'no' to boolean and map to backend property
                    const { vatOnCollectedPayments, ...restValues } = stepValues

                    return {
                        ...restValues,
                        CalculatesVatUponPayment: vatOnCollectedPayments === 'yes',
                    }
                },
            },
            {
                id: 'success',
                title: 'V2.Onboarding.Step5.title',
                subtitle: 'V2.Onboarding.Step5.subtitle',
                shouldSkip: formValues => false,
                fields: [],
                validationSchema: Yup.object(),
                render: (formikProps: FormikProps<OnboardingCompanyData>) => <OnboardingCompleted formikProps={formikProps} />,
            },
        ],
        [t, bankAccountSchema, onboardingData, croatianIbanValidation, regularIbanValidation]
    )

    // Filter visible steps based on shouldSkip conditions
    // Pass current form values so steps can dynamically show/hide based on user input
    const visibleSteps = stepConfigs.filter(step => step.shouldSkip(formValues) === false)
    const currentStep = visibleSteps[currentStepIndex]
    const isLastFormStep = currentStep.id === 'notes-and-legal'
    const isSuccessStep = currentStep.id === 'success'
    const isFirstStep = currentStepIndex === 0

    const handleNext = async (formikProps: FormikProps<OnboardingCompanyData>) => {
        const isValid = await formikProps.validateForm()
        const currentStepFields = currentStep.fields

        const stepErrors = currentStepFields.some(field => isValid[field])

        if (!stepErrors) {
            // Extract step values from form
            let stepValues = extractStepValues(currentStepFields, formikProps.values)

            // Apply step-specific transformations if defined
            if (currentStep.beforeSubmit) {
                stepValues = currentStep.beforeSubmit(stepValues, formikProps.values, onboardingData)
            }

            await updateOnboardingData(stepValues, true)
            setFormValues({ ...formValues, ...formikProps.values }) // Update form values for dynamic step filtering
            formikProps.setErrors({})
            formikProps.setTouched({})
            setCurrentStepIndex(prev => Math.min(prev + 1, visibleSteps.length - 1))
            scrollModalToTop()
        } else {
            // Mark all step fields as touched to show validation errors
            const touchedFields = markStepFieldsAsTouched(currentStepFields, formikProps.values)
            formikProps.setTouched(touchedFields)
        }
    }

    const handleBack = () => {
        setCurrentStepIndex(prev => Math.max(prev - 1, 0))
        scrollModalToTop()
    }

    const handleSubmit = async (values: OnboardingCompanyData) => {
        if (isLastFormStep) {
            const currentStepFields = currentStep.fields
            let stepValues = extractStepValues(currentStepFields, values)

            // Apply step-specific transformations if defined (consistent with handleNext)
            if (currentStep.beforeSubmit) {
                stepValues = currentStep.beforeSubmit(stepValues, values, onboardingData)
            }

            const receiptPaymentTerms = buildDefaultReceiptPaymentTerms(values, t)
            const tenderNote = generateTenderNote(values, t)

            stepValues.defaultReceiptPaymentTerms = receiptPaymentTerms
            stepValues.defaultTenderNote = tenderNote
            await updateOnboardingData(stepValues)

            const secondBankAccount = values.bankAccounts && values.bankAccounts.length > 1 ? values.bankAccounts[1] : ''
            // @ts-ignore this is a hacky way to save the data
            CompanySettingsApi.SaveEInvoiceConfiguration({
                legalCompanyName:
                    receiptPaymentTerms.length > MAX_LEGAL_COMPANY_NAME_LENGTH
                        ? receiptPaymentTerms.slice(0, MAX_LEGAL_COMPANY_NAME_LENGTH)
                        : receiptPaymentTerms,
                secondBankAccount: secondBankAccount,
            })

            setCurrentStepIndex(prev => Math.min(prev + 1, visibleSteps.length - 1))
            setShowOnboardingOnStart(false)
        } else if (isSuccessStep) {
            setShowOnboardingOnStart(false)
            closeOnboardingModal()
        }
    }

    return (
        <>
            <DialogHeader>
                <DialogTitle>
                    {currentStep.id !== 'success' && <StepIndicator currentStep={currentStepIndex + 1} totalSteps={visibleSteps.length} />}
                    <h2 className="text-lg mt-6 mb-4 p-0 flex gap-2">
                        {isSuccessStep && (
                            <div>
                                <CircleWavyCheck className="text-blue40 size-7 bg-blue20 p-1 rounded-sm relative -top-1" />{' '}
                            </div>
                        )}
                        {t(typeof currentStep.title === 'function' ? currentStep.title(formValues) : currentStep.title)}
                    </h2>
                </DialogTitle>

                <div>
                    <div className="text-grey90 mb-5">
                        {t(typeof currentStep.subtitle === 'function' ? currentStep.subtitle(formValues) : currentStep.subtitle)}
                    </div>
                </div>
            </DialogHeader>

            {isSubmittingOnboardingData ? (
                <div className="spinner-container">
                    <Spinner />
                </div>
            ) : (
                <Formik
                    initialValues={formValues}
                    validationSchema={currentStep.validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {formikProps => (
                        <Form>
                            <div>
                                {!onboardingData ? (
                                    <div style={{ padding: '2rem', textAlign: 'center' }}>{t('Allaround.Message.loading')}</div>
                                ) : (
                                    currentStep.render(formikProps)
                                )}
                            </div>

                            <ModalButtons
                                isFirstFormStep={isFirstStep}
                                isLastFormStep={isLastFormStep}
                                isSuccessStep={isSuccessStep}
                                onHandleNext={() => {
                                    handleNext(formikProps)
                                }}
                                onHandleBack={() => {
                                    handleBack()
                                }}
                                onHandleComplete={() => {
                                    closeOnboardingModal()
                                    setShowOnboardingOnStart(false)
                                }}
                            />
                        </Form>
                    )}
                </Formik>
            )}
        </>
    )
}

export function ModalButtons({
    isFirstFormStep,
    isLastFormStep,
    isSuccessStep,
    onHandleNext,
    onHandleBack,
    onHandleComplete,
}: {
    isFirstFormStep: boolean
    isLastFormStep: boolean
    isSuccessStep: boolean
    onHandleNext: () => void
    onHandleBack: () => void
    onHandleComplete: () => void
}) {
    const { t } = useOfferionTranslation()

    // Success step - only show complete button
    if (isSuccessStep) {
        return (
            <div className="flex justify-end gap-4 mt-6">
                <Button
                    type="button"
                    onClick={e => {
                        e.preventDefault()
                        onHandleComplete()
                    }}
                >
                    {t('Allaround.Button.finish')}
                </Button>
            </div>
        )
    }

    // Regular form steps
    return (
        <div className="flex justify-end gap-4 mt-6">
            {!isFirstFormStep && (
                <Button
                    className="mr-auto"
                    onClick={e => {
                        e.preventDefault()
                        onHandleBack()
                    }}
                    type="button"
                    variant="secondary"
                >
                    {t('Allaround.Button.back')}
                </Button>
            )}

            <Button
                type={isLastFormStep ? 'submit' : 'button'}
                onClick={
                    isLastFormStep
                        ? undefined
                        : e => {
                              e.preventDefault()
                              onHandleNext()
                          }
                }
            >
                {t('Allaround.Button.next')}
            </Button>
        </div>
    )
}

// Step Components
const CompanyAndAddressStep: React.FC<{ formikProps: FormikProps<OnboardingCompanyData> }> = ({ formikProps }) => {
    const { t } = useOfferionTranslation()
    const countryOptions = useStaticCountryOptions()

    const isCroatia = formikProps.values.country === 'HR'

    return (
        <div className="form-step flex flex-col gap-4">
            <FormField
                name="name"
                required
                label={t('V2.Onboarding.Step1.companyName')}
                placeholder={t('V2.Onboarding.Step1.companyNamePlaceholder')}
                component={FormInput}
            />

            {isCroatia ? (
                <FormSelect
                    name="companyLegalType"
                    required
                    label={t('V2.Onboarding.Step1.companyLegalType')}
                    placeholder={t('V2.Onboarding.Step1.companyLegalTypePlaceholder')}
                    options={[
                        { value: 'doo', label: t('Allaround.CompanyLegalType.doo') },
                        { value: 'jdoo', label: t('Allaround.CompanyLegalType.jdoo') },
                        { value: 'dd', label: t('Allaround.CompanyLegalType.dd') },
                        { value: 'obrt', label: t('Allaround.CompanyLegalType.obrt') },
                        { value: 'other', label: t('Allaround.CompanyLegalType.other') },
                    ]}
                />
            ) : null}

            <FormField
                name="street"
                required
                label={t('V2.Onboarding.Step1.streetName')}
                placeholder={t('V2.Onboarding.Step1.streetNamePlaceholder')}
                component={FormInput}
            />

            <div className="flex gap-4">
                <FormField
                    name="streetNumber"
                    required
                    label={t('V2.Onboarding.Step1.streetNumber')}
                    placeholder={t('V2.Onboarding.Step1.streetNumberPlaceholder')}
                    component={FormInput}
                />
                <FormField
                    name="postalCode"
                    required
                    label={t('V2.Onboarding.Step1.postalCode')}
                    placeholder={t('V2.Onboarding.Step1.postalCodePlaceholder')}
                    component={FormInput}
                />
            </div>

            <FormField
                name="city"
                required
                label={t('V2.Onboarding.Step1.city')}
                placeholder={t('V2.Onboarding.Step1.cityPlaceholder')}
                component={FormInput}
            />

            <FormCombobox
                name="country"
                required
                label={t('V2.Onboarding.Step1.country')}
                placeholder={t('V2.Onboarding.Step1.countryPlaceholder')}
                options={countryOptions}
            />
        </div>
    )
}

const ContactInfoStep: React.FC<{ formikProps: FormikProps<OnboardingCompanyData> }> = ({ formikProps }) => {
    const { t } = useOfferionTranslation()

    const isCroatia = formikProps.values.country === 'HR'
    const ibanTooltip = isCroatia ? t('V2.Onboarding.Step2.ibanCroatiaTooltip') : t('V2.Onboarding.Step2.ibanInternationalTooltip')

    return (
        <div className="form-step flex flex-col gap-4">
            <FormField
                autoFocus
                name="telephone"
                type="tel"
                inputMode="tel"
                label={t('V2.Onboarding.Step2.phone')}
                placeholder={t('V2.Onboarding.Step2.phonePlaceholder')}
                component={FormInput}
            />

            <FormField
                name="mobile"
                type="tel"
                inputMode="tel"
                label={t('V2.Onboarding.Step2.mobilePhone')}
                placeholder={t('V2.Onboarding.Step2.mobilePhonePlaceholder')}
                component={FormInput}
            />

            <FormField
                name="contactEmail"
                type="email"
                inputMode="email"
                label={t('V2.Onboarding.Step2.email')}
                placeholder={t('V2.Onboarding.Step2.emailPlaceholder')}
                component={FormInput}
                tooltip={t('V2.Onboarding.Step2.emailTooltip')}
            />

            <FormField
                name="website"
                type="url"
                inputMode="url"
                label={t('V2.Onboarding.Step2.websiteUrl')}
                placeholder={t('V2.Onboarding.Step2.websiteUrlPlaceholder')}
                component={FormInput}
            />

            <FieldArray name="bankAccounts">
                {arrayHelper => {
                    return (
                        <div className="flex flex-col gap-3">
                            {formikProps.values.bankAccounts?.map((bankAccount, index) => {
                                return (
                                    <div key={index}>
                                        <FormField
                                            name={`bankAccounts[${index}]`}
                                            type="text"
                                            // show only the first label and tooltip
                                            label={index === 0 ? t('V2.Onboarding.Step2.iban') : undefined}
                                            placeholder={t('V2.Onboarding.Step2.ibanPlaceholder')}
                                            tooltip={index === 0 ? ibanTooltip : undefined}
                                            component={FormInput}
                                        />
                                    </div>
                                )
                            })}

                            <Button onClick={() => arrayHelper.push('')} type="button" variant="ghost" className="text-blue40">
                                <Plus /> {t('V2.Onboarding.Step2.addNewIban')}
                            </Button>
                        </div>
                    )
                }}
            </FieldArray>
        </div>
    )
}

const TaxAndCurrencyStep: React.FC<{ formikProps: FormikProps<OnboardingCompanyData>; onboardingData: OnboardingCompanyData }> = ({
    formikProps,
    onboardingData,
}) => {
    const { t } = useOfferionTranslation()
    const currencyOptions = useCurrencyOptions()

    const isCroatia = formikProps.values.country === 'HR'
    const isAccountingVat = onboardingData.isAccountingVat

    return (
        <div className="form-step flex flex-col gap-4">
            {/* if country is Croatia, the currency is always EUR */}
            {isCroatia === false && (
                <FormSelect
                    name="defaultCurrency"
                    required
                    label={t('V2.Onboarding.Step3.currency')}
                    placeholder={t('V2.Onboarding.Step3.currencyPlaceholder')}
                    options={currencyOptions}
                />
            )}

            {/* Only show tax field if accounting VAT is enabled */}
            {isAccountingVat && (
                <FormField
                    name="defaultVat"
                    type="text"
                    label={t('V2.Onboarding.Step3.tax')}
                    placeholder={t('V2.Onboarding.Step3.taxPlaceholder')}
                    component={CurrencyInput}
                />
            )}
        </div>
    )
}

const NotesAndLegalStep: React.FC<{ formikProps: FormikProps<OnboardingCompanyData>; onboardingData: OnboardingCompanyData }> = ({
    formikProps,
    onboardingData,
}) => {
    const { t } = useOfferionTranslation()

    let companyLegalTypeSpecificInputs = null

    switch (formikProps.values.companyLegalType) {
        case 'doo':
        case 'jdoo':
            companyLegalTypeSpecificInputs = <NotesAndLegalForCompany formikProps={formikProps} />
            break
        case 'dd':
            companyLegalTypeSpecificInputs = <NotesAndLegalForCorporation formikProps={formikProps} />
            break
        case 'obrt':
            companyLegalTypeSpecificInputs = <NotesAndLegalForTrade formikProps={formikProps} />
            break
        default:
            companyLegalTypeSpecificInputs = null
    }

    return (
        <div className="form-step form-step--notes flex flex-col gap-4">
            <div className="bg-grey10 p-[18px] rounded-md divide-y">
                {onboardingData.isAccountingVat && (
                    <div className="pb-2">
                        <FormRadioInput
                            name="vatOnCollectedPayments"
                            label={t('V2.Onboarding.Step4.vatOnCollectedPayments')}
                            options={[
                                { value: 'yes', label: t('Allaround.Button.yes') },
                                { value: 'no', label: t('Allaround.Button.no') },
                            ]}
                        />
                    </div>
                )}

                <div className={onboardingData.isAccountingVat ? 'pt-2' : ''}>
                    <FormRadioInput
                        name="workingWithIndividuals"
                        label={t('V2.Onboarding.Step4.workingWithIndividuals')}
                        options={[
                            { value: 'yes', label: t('Allaround.Button.yes') },
                            { value: 'no', label: t('Allaround.Button.no') },
                        ]}
                    />
                </div>
            </div>

            {companyLegalTypeSpecificInputs}
        </div>
    )
}

function NotesAndLegalForCompany({ formikProps }: { formikProps: FormikProps<OnboardingCompanyData> }) {
    const { t } = useOfferionTranslation()

    return (
        <>
            <FormField
                name="commercialCourtName"
                type="text"
                required
                label={t('V2.Onboarding.Step4.commercialCourtName')}
                placeholder={t('V2.Onboarding.Step4.commercialCourtNamePlaceholder')}
                component={FormInput}
            />

            <FormField
                name="companyRegistrationNumber"
                type="text"
                required
                label={t('V2.Onboarding.Step4.companyRegistrationNumber')}
                placeholder={t('V2.Onboarding.Step4.companyRegistrationNumberPlaceholder')}
                component={FormInput}
            />

            <FormField
                name="shareCapitalAmount"
                type="text"
                required
                label={t('V2.Onboarding.Step4.shareCapitalAmount')}
                placeholder={t('V2.Onboarding.Step4.shareCapitalAmountPlaceholder')}
                component={CurrencyInput}
            />

            <div className="bg-grey10 p-[18px] rounded-md">
                <div className="">
                    <FormRadioInput
                        name="shareCapitalFullyPaid"
                        label={t('V2.Onboarding.Step4.shareCapitalFullyPaid')}
                        options={[
                            { value: 'yes', label: t('Allaround.Button.yes') },
                            { value: 'no', label: t('Allaround.Button.no') },
                        ]}
                    />
                </div>

                {formikProps.values.shareCapitalFullyPaid === 'no' && (
                    <div className="pt-4">
                        <FormField
                            name="unpaidShareCapitalAmount"
                            type="text"
                            required
                            label={t('V2.Onboarding.Step4.unpaidShareCapitalAmount')}
                            placeholder={t('V2.Onboarding.Step4.unpaidShareCapitalAmountPlaceholder')}
                            component={CurrencyInput}
                        />
                    </div>
                )}
            </div>

            <FieldArray name="boardMembers">
                {arrayHelper => {
                    return (
                        <div className="flex flex-col gap-3">
                            {formikProps.values.boardMembers?.map((boardMember, index) => {
                                return (
                                    <div key={index}>
                                        <FormField
                                            name={`boardMembers[${index}]`}
                                            type="text"
                                            // show only the first label and tooltip
                                            label={index === 0 ? t('V2.Onboarding.Step4.boardMembers') : undefined}
                                            required={index === 0}
                                            placeholder={t('V2.Onboarding.Step4.boardMembersPlaceholder')}
                                            component={FormInput}
                                        />
                                    </div>
                                )
                            })}

                            <Button onClick={() => arrayHelper.push('')} type="button" variant="ghost" className="text-blue40">
                                <Plus /> {t('V2.Onboarding.Step4.addBoardMember')}
                            </Button>
                        </div>
                    )
                }}
            </FieldArray>

            <div className="bg-grey10 p-[18px] rounded-md">
                <div className="">
                    <FormRadioInput
                        name="doYouHaveAnAdvisoryBoard"
                        label={t('V2.Onboarding.Step4.doYouHaveAnAdvisoryBoard')}
                        options={[
                            { value: 'yes', label: t('Allaround.Button.yes') },
                            { value: 'no', label: t('Allaround.Button.no') },
                        ]}
                    />
                </div>

                {formikProps.values.doYouHaveAnAdvisoryBoard === 'yes' && (
                    <div className="pt-4">
                        <FormField
                            name="advisoryBoardPresidentName"
                            type="text"
                            required
                            // label={t('V2.Onboarding.Step4.unpaidShareCapitalAmount')}
                            placeholder={t('V2.Onboarding.Step4.advisoryBoardPresidentNamePlaceholder')}
                            component={FormInput}
                        />
                    </div>
                )}
            </div>
        </>
    )
}

function NotesAndLegalForCorporation({ formikProps }: { formikProps: FormikProps<OnboardingCompanyData> }) {
    const { t } = useOfferionTranslation()

    return (
        <>
            <NotesAndLegalForCompany formikProps={formikProps} />

            <FormField
                name="numberOfIssuedShares"
                type="text"
                required
                label={t('V2.Onboarding.Step4.numberOfIssuedShares')}
                // placeholder={t('V2.Onboarding.Step4.numberOfIssuedSharesPlaceholder')}
                component={FormInput}
            />

            <FormField
                name="nominalValuePerShare"
                type="text"
                required
                label={t('V2.Onboarding.Step4.nominalValuePerShare')}
                // placeholder={t('V2.Onboarding.Step4.nominalValuePerSharePlaceholder')}
                component={FormInput}
            />
        </>
    )
}

function NotesAndLegalForTrade({ formikProps }: { formikProps: FormikProps<OnboardingCompanyData> }) {
    const { t } = useOfferionTranslation()

    return (
        <>
            <div className="bg-grey10 p-[18px] rounded-md">
                <div className="">
                    <FormRadioInput
                        name="differentAddressForOwnerAndTrade"
                        label={t('V2.Onboarding.Step4.differentAddressForOwnerAndTrade')}
                        options={[
                            { value: 'yes', label: t('Allaround.Button.yes') },
                            { value: 'no', label: t('Allaround.Button.no') },
                        ]}
                    />
                </div>

                {/* @ts-ignore */}
                {formikProps.values.differentAddressForOwnerAndTrade === 'yes' && (
                    <div className="pt-4">
                        <FormField
                            name="tradeOwnerAddress"
                            type="text"
                            required
                            label={t('V2.Onboarding.Step4.tradeOwnerAddress')}
                            // placeholder={t('V2.Onboarding.Step4.tradeOwnerAddressPlaceholder')}
                            component={FormInput}
                        />
                    </div>
                )}
            </div>
        </>
    )
}

function OnboardingCompleted({ formikProps }: { formikProps: FormikProps<OnboardingCompanyData> }) {
    const { t } = useOfferionTranslation()

    return (
        <>
            <div className="bg-blue20 text-blue40 rounded-lg p-4" dangerouslySetInnerHTML={{ __html: t('V2.Onboarding.Step5.info') }}></div>
        </>
    )
}

function initializeFormValues(onboardingData: OnboardingCompanyData): OnboardingCompanyData {
    // There's some data that's prefilled for new companies with empty default values,
    // this will strip those values to force users to fill them in during onboarding.
    let bankAccounts

    // Currently company settings only has a bankAccount.
    // This will get handled eventually, so we want to be ready for both cases.
    if (onboardingData.bankAccounts && onboardingData.bankAccounts.length > 0) {
        bankAccounts = onboardingData.bankAccounts
    } else if (
        onboardingData.bankAccounts &&
        onboardingData.bankAccounts.length === 0 &&
        onboardingData.bankAccount &&
        onboardingData.bankAccount !== ''
    ) {
        bankAccounts = [onboardingData.bankAccount]
    } else {
        bankAccounts = ['']
    }

    return {
        ...onboardingData,
        // These values are going to be overriden to empty if they contain default values,
        // this forces users to change them to something meaningful during the onboarding process.
        oib: onboardingData.oib !== '' && onboardingData.oib !== null && onboardingData.oib !== '12345678901' ? onboardingData.oib : '',
        street:
            onboardingData.street !== '' && onboardingData.street !== null && onboardingData.street !== 'Ulica Tvrtke'
                ? onboardingData.street
                : '',
        city:
            onboardingData.city !== '' && onboardingData.city !== null && onboardingData.city !== 'Mjesto Tvrtke'
                ? onboardingData.city
                : '',
        streetNumber:
            onboardingData.streetNumber !== '' && onboardingData.streetNumber !== null && onboardingData.streetNumber !== '123a'
                ? onboardingData.streetNumber
                : '',
        postalCode:
            onboardingData.postalCode !== '' && onboardingData.postalCode !== null && onboardingData.postalCode !== '12345'
                ? onboardingData.postalCode
                : '',
        // companyLegalType: undefined,
        companyLegalType: undefined,
        bankAccounts: bankAccounts,
        vatOnCollectedPayments: 'no',
        workingWithIndividuals: 'no',
        commercialCourtName: '',
        companyRegistrationNumber: '',
        shareCapitalAmount: '',
        shareCapitalFullyPaid: 'yes',
        unpaidShareCapitalAmount: '',
        defaultCurrency: Currency.EUR,
    }
}

function buildDefaultReceiptPaymentTerms(onboardingData: OnboardingCompanyData, t: (key: string, options?: any) => string): string {
    let paymentNote
    const isCompany =
        onboardingData.companyLegalType === 'doo' || onboardingData.companyLegalType === 'jdoo' || onboardingData.companyLegalType === 'dd'

    if (onboardingData.isAccountingVat && onboardingData.vatOnCollectedPayments === 'yes') {
        paymentNote = 'PDV obračunavamo prema naplaćenoj naknadi.'
    } else if (onboardingData.isAccountingVat && onboardingData.vatOnCollectedPayments === 'no') {
        paymentNote = 'PDV obračunavamo prema izdanim računima.'
    } else {
        paymentNote = 'Oslobođeno PDV-a po članku 90 Zakona o PDV-u.'
    }

    if (onboardingData.workingWithIndividuals === 'yes') {
        paymentNote +=
            '\n\nUpozoravate se da u slučaju neispunjenja dospjele novčane obveze možemo zatražiti određivanje ovrhe temeljem vjerodostojne isprave (ovog računa).'
    }

    const bankInfoString = onboardingData.bankAccounts
        ?.map(bankAccount => {
            const bankInfo = getBankInfo(bankAccount)
            if (!bankInfo) return `Žiro-račun (IBAN): ${bankAccount}`

            return `Žiro-račun (IBAN): ${bankAccount}, ${bankInfo.bankName}, ${bankInfo.bankAddress}${bankInfo.swiftNumber ? `, SWIFT (BIC): ${bankInfo.swiftNumber}` : ''}.`
        })
        .join('\n')

    if (isCompany) {
        paymentNote += `\n\nRegistarski sud: ${onboardingData.commercialCourtName}, MBS: ${onboardingData.companyRegistrationNumber}.
${bankInfoString}`

        if (onboardingData.shareCapitalFullyPaid === 'yes') {
            paymentNote += `\nTemeljni kapital ${currencyAsText(onboardingData.shareCapitalAmount!)} €, uplaćen u cijelosti.`
        } else {
            paymentNote += `\nTemeljni kapital ${currencyAsText(onboardingData.shareCapitalAmount!)} €, od čega nije uplaćeno ${currencyAsText(onboardingData.unpaidShareCapitalAmount!)} €.`
        }
    } else if (onboardingData.companyLegalType === 'obrt') {
        if (onboardingData.differentAddressForOwnerAndTrade === 'yes') {
            paymentNote += `\n\nVlasnik obrta: ${onboardingData.userFullName}, ${onboardingData.tradeOwnerAddress}. ${bankInfoString}`
        } else if (onboardingData.differentAddressForOwnerAndTrade === 'no') {
            paymentNote += `\n\nVlasnik obrta: ${onboardingData.userFullName}. ${bankInfoString}`
        }
    }

    if (isCompany && onboardingData.boardMembers && onboardingData.boardMembers.length === 1) {
        paymentNote += `\n\nČlan uprave: ${onboardingData.boardMembers[0]}.`
    } else if (isCompany && onboardingData.boardMembers && onboardingData.boardMembers.length > 1) {
        paymentNote += `\n\nČlanovi uprave: ${onboardingData.boardMembers.join(', ')}.`
    }

    if (isCompany && onboardingData.doYouHaveAnAdvisoryBoard === 'yes') {
        paymentNote += `\n\nPredsjednik nadzornog odbora: ${onboardingData.advisoryBoardPresidentName!}.`
    }

    if (onboardingData.companyLegalType === 'dd') {
        paymentNote += `\n\nUkupan broj izdanih dionica ${onboardingData.numberOfIssuedShares}, nominalni iznos dionice ${currencyAsText(onboardingData.nominalValuePerShare!)} €.`
    }

    return paymentNote
}

function generateTenderNote(onboardingData: OnboardingCompanyData, t: (key: string, options?: any) => string): string {
    const iban = onboardingData.bankAccounts?.[0]
    return t('Allaround.CompanySettings.paymentNote', { iban: iban })
}
